import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { DoxMascot } from "@/components/Logo";

/**
 * DoxWorkPerformance — the one-shot Assign → Depart → Work → Return animation
 * that plays whenever the customer hands a task to Dox.
 *
 * Storytelling contract:
 *   1. ASSIGN  — Dox looks up, tiny signature nod, says "I've got it.", picks up folder.
 *   2. DEPART  — WHOOSH off-screen right with a soft brand-blue streak.
 *   3. WORK    — Apple-style status card cycles through live progress stages
 *                (minimum 2s floor + real backend duration; never faked).
 *   4. RETURN  — Streak inbound, Dox slides back to center, straightens.
 *   5. DELIVER — Hands over the completed folder, says one calm line.
 *
 * Motion rules (per Doug's directive):
 *   • Signature gesture is ONE tiny nod. Never dancing, spinning, celebration.
 *   • Never fake completion. Dox only returns when the real work is done.
 *   • Never fake percentage bars. Never countdown timers.
 *   • Quiet confidence over comedy.
 */

const WORK_MIN_MS = 2000; // "Feels like real work was performed" floor.

const DEFAULT_STAGES = [  { label: "Researching requirements",   emoji: "🔍" },
  { label: "Verifying state records",    emoji: "✅" },
  { label: "Reviewing your documents",   emoji: "📄" },
  { label: "Checking compliance",        emoji: "🛡️" },
  { label: "Preparing filing",           emoji: "✍️" },
];

/**
 * @typedef {Object} PerformanceConfig
 * @property {string}            [ackLine]        Speech line while Dox picks up the folder. Default: "I've got it."
 * @property {string}            [deliverLine]    Speech line on return. Default: "Done."
 * @property {Array}             [stages]         Working-state chips (label + emoji or icon).
 * @property {() => Promise<T>}  run              The real backend call. Its resolved value becomes onResolved's arg.
 * @property {(res:T)=>void}     [onResolved]     Called after Dox returns and delivers. Use for toasts/side effects.
 * @property {(err:Error)=>void} [onError]
 */

export default function DoxWorkPerformance({ open, config, onClose }) {
  const [phase, setPhase] = useState("assign"); // assign → depart → work → return → deliver → done
  const [stageIndex, setStageIndex] = useState(0);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const stages = config?.stages || DEFAULT_STAGES;

  // Kick off the animation timeline when opened.
  const startedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      setPhase("assign");
      setStageIndex(0);
      setError(null);
      setResult(null);
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;

    const t0 = performance.now();
    let stageTimer;
    let cancelled = false;

    const stepStages = () => {
      // Advance through stages while we're still in "work" phase.
      // Real work continues until the backend resolves — we cycle stages to
      // keep the customer's attention on the story, never faking completion.
      stageTimer = setInterval(() => {
        if (cancelled) return;
        setStageIndex((i) => (i + 1) % stages.length);
      }, 1400);
    };

    (async () => {
      // Phase 1 — ASSIGN (~1.15s: 200ms look-up, 250ms nod, 300ms speech, 400ms folder-lift)
      await sleep(1150);
      if (cancelled) return;

      // Phase 2 — DEPART (~500ms whoosh)
      setPhase("depart");
      await sleep(500);
      if (cancelled) return;

      // Phase 3 — WORK — kick off backend and stage rotation together.
      setPhase("work");
      stepStages();

      let backendResult;
      let backendError;
      try {
        backendResult = await Promise.resolve(config?.run ? config.run() : null);
      } catch (e) {
        backendError = e;
      }

      // Enforce the 2s minimum floor so it feels deliberate.
      const elapsed = performance.now() - t0 - 1150 - 500;
      if (elapsed < WORK_MIN_MS) {
        await sleep(WORK_MIN_MS - elapsed);
      }
      if (cancelled) return;
      clearInterval(stageTimer);

      if (backendError) {
        setError(backendError);
        setPhase("error");
        return;
      }
      setResult(backendResult);

      // Phase 4 — RETURN (~600ms whoosh-in)
      setPhase("return");
      await sleep(600);
      if (cancelled) return;

      // Phase 5 — DELIVER (~1.6s: settle, straighten, hand over, speak)
      setPhase("deliver");
      await sleep(1600);
      if (cancelled) return;

      // Auto-close after a brief moment on the deliver frame.
      setPhase("done");
      await sleep(400);
      if (cancelled) return;
      config?.onResolved?.(backendResult);
      onClose?.();
    })();

    return () => {
      cancelled = true;
      clearInterval(stageTimer);
    };
  }, [open, config, onClose, stages.length]);

  const acknowledgeError = useCallback(() => {
    config?.onError?.(error);
    onClose?.();
  }, [config, error, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="dox-work-performance"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[110] bg-[#050812]/95 backdrop-blur-xl flex items-center justify-center overflow-hidden"
        >
          {/* Soft brand aura */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-25"
            style={{
              background:
                "radial-gradient(circle at 50% 55%, rgba(30,120,255,0.35), transparent 55%)",
            }}
          />

          <div className="relative w-full max-w-2xl px-6 py-16 text-center">
            <DoxStage phase={phase} ackLine={config?.ackLine || "I've got it."} />

            {/* Live status card, only while working */}
            <AnimatePresence mode="wait">
              {phase === "work" && (
                <motion.div
                  key="status"
                  data-testid="dox-work-status"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 12 }}
                  transition={{ duration: 0.5, ease: [0.33, 1, 0.68, 1] }}
                  className="mt-12 mx-auto max-w-md rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md px-6 py-5 text-left"
                >
                  <div className="text-[10px] uppercase tracking-[0.28em] text-dox-orange font-bold mb-3">
                    Dox is working
                  </div>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={stageIndex}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.4 }}
                      data-testid="dox-work-stage"
                      className="flex items-center gap-3"
                    >
                      <StageVisual code={stages[stageIndex]?.code} fallbackEmoji={stages[stageIndex]?.emoji} />
                      <span className="text-white/90 text-base font-medium">
                        {stages[stageIndex]?.label}
                      </span>
                    </motion.div>
                  </AnimatePresence>
                </motion.div>
              )}

              {phase === "deliver" && (
                <motion.div
                  key="deliver"
                  data-testid="dox-work-deliver"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: [0.33, 1, 0.68, 1] }}
                  className="mt-12 font-display font-black text-white text-4xl sm:text-5xl tracking-[-0.02em] leading-[1.05]"
                >
                  {config?.deliverLine || "Done."}
                </motion.div>
              )}

              {phase === "error" && (
                <motion.div
                  key="error"
                  data-testid="dox-work-error"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="mt-12"
                >
                  <div className="text-white/85 text-lg max-w-md mx-auto">
                    {"I hit a snag. Nothing was submitted — you can try again in a moment."}
                  </div>
                  <button
                    data-testid="dox-work-error-close"
                    onClick={acknowledgeError}
                    className="mt-8 rounded-full bg-dox-orange hover:bg-dox-orangeHover text-white font-semibold text-base px-8 py-3.5 min-h-[48px] transition-all"
                  >
                    Got it
                  </button>
                </motion.div>
              )}

              {phase === "needs_you" && (
                <NeedsYouCard
                  key="needs_you"
                  needs={config?.needsInfoOverride || result?.data?.needs_info || result?.needs_info}
                  onUploaded={() => {
                    config?.onResolved?.(result);
                    onClose?.();
                  }}
                  onDismiss={() => onClose?.()}
                />
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* -------------------------------------------------------------------------- */
/* Dox on stage — the performance itself                                      */
/* -------------------------------------------------------------------------- */

function DoxStage({ phase, ackLine }) {
  // Positions per phase, tuned to feel deliberate rather than cartoonish.
  const stagePositions = {
    assign:  { x: 0,   scale: 1,   opacity: 1 },
    depart:  { x: 900, scale: 0.7, opacity: 0 },
    work:    { x: 900, scale: 0.7, opacity: 0 }, // Dox is off-stage while working
    return:  { x: 0,   scale: 1,   opacity: 1 },
    deliver: { x: 0,   scale: 1,   opacity: 1 },
    done:    { x: 0,   scale: 1,   opacity: 1 },
    error:   { x: 0,   scale: 1,   opacity: 1 },
  };
  const p = stagePositions[phase] || stagePositions.assign;
  const emotion = phase === "deliver" || phase === "done"
    ? "done"
    : phase === "return"
    ? "greeting"
    : phase === "assign"
    ? "greeting"
    : phase === "error"
    ? "reading"
    : "idle";

  return (
    <div className="relative h-64 sm:h-72 flex items-center justify-center">
      {/* Blue speed streak that appears during depart + return */}
      <AnimatePresence>
        {(phase === "depart" || phase === "return") && (
          <motion.div
            key={phase + "-streak"}
            aria-hidden="true"
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            exit={{ opacity: 0, scaleX: 0 }}
            transition={{ duration: 0.45, ease: [0.33, 1, 0.68, 1] }}
            className="pointer-events-none absolute top-1/2 -translate-y-1/2 h-[3px] w-[420px] origin-left"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(30,120,255,0) 5%, rgba(30,120,255,0.9) 45%, rgba(90,168,255,1) 70%, transparent 100%)",
              filter: "blur(0.5px)",
              boxShadow: "0 0 30px rgba(30,120,255,0.6)",
              transform:
                phase === "depart"
                  ? "translateX(-30%) scaleX(1)"
                  : "translateX(-70%) scaleX(-1)",
            }}
          />
        )}
      </AnimatePresence>

      {/* Signature nod — a subtle down-then-up on the assign phase */}
      <motion.div
        animate={{
          x: p.x,
          scale: p.scale,
          opacity: p.opacity,
        }}
        transition={{
          x: {
            duration: phase === "depart" ? 0.5 : phase === "return" ? 0.6 : 0.6,
            ease: phase === "depart"
              ? [0.65, 0, 1, 0.4] // ease-in — accelerating out
              : phase === "return"
              ? [0, 0.4, 0.2, 1]  // ease-out — decelerating in
              : [0.33, 1, 0.68, 1],
          },
          scale: { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
          opacity: { duration: phase === "depart" ? 0.5 : 0.35 },
        }}
        className="relative"
      >
        {/* Assign phase adds the tiny signature nod */}
        <motion.div
          animate={
            phase === "assign"
              ? { y: [0, 0, 4, 0], rotate: [0, 0, 2, 0] }
              : phase === "return"
              ? { y: [10, 0], rotate: [-1, 0] }
              : { y: 0, rotate: 0 }
          }
          transition={{
            duration: phase === "assign" ? 1.05 : phase === "return" ? 0.55 : 0.4,
            times: phase === "assign" ? [0, 0.35, 0.6, 1] : undefined,
            ease: [0.33, 1, 0.68, 1],
          }}
        >
          <DoxMascot size={160} emotion={emotion} />
        </motion.div>

        {/* Folder Dox carries — a small paper motif that follows him */}
        <FolderProp phase={phase} />

        {/* Speech bubble */}
        <AnimatePresence>
          {phase === "assign" && (
            <SpeechBubble key="assign" delay={0.55} line={ackLine} testid="dox-work-ack" />
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

function FolderProp({ phase }) {
  // The folder appears in Dox's hand starting mid-assign, is carried off,
  // then arrives back in the deliver phase as the completed receipt.
  const visible =
    phase === "assign" || phase === "depart" || phase === "return" || phase === "deliver" || phase === "done";
  const isDeliver = phase === "deliver" || phase === "done";
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key={"folder-" + (isDeliver ? "receipt" : "in")}
          initial={{ opacity: 0, y: 10, scale: 0.85 }}
          animate={{ opacity: 1, y: isDeliver ? 6 : 0, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.55,
            delay: phase === "assign" ? 0.45 : 0,
            ease: [0.33, 1, 0.68, 1],
          }}
          className="absolute -bottom-2 right-0 translate-x-6"
          data-testid={isDeliver ? "dox-work-receipt" : "dox-work-folder"}
          aria-hidden="true"
        >
          <div
            className="rounded-md border shadow-lg"
            style={{
              width: 44,
              height: 34,
              background: isDeliver ? "#FFFFFF" : "#F3F6FF",
              borderColor: "#0B1B4D",
              boxShadow: "0 6px 18px rgba(11,27,77,0.28)",
            }}
          >
            {isDeliver && (
              <div className="pt-1.5 px-1.5 space-y-[3px]">
                <div className="h-[3px] rounded-sm" style={{ background: "#1E78FF" }} />
                <div className="h-[2px] rounded-sm bg-[#0B1B4D]/50 w-4/5" />
                <div className="h-[2px] rounded-sm bg-[#0B1B4D]/40 w-3/5" />
                <div className="h-[2px] rounded-sm bg-emerald-500/70 w-1/2" />
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function SpeechBubble({ line, delay = 0, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.45, delay, ease: [0.33, 1, 0.68, 1] }}
      className="absolute -top-3 left-1/2 -translate-x-1/2 -translate-y-full whitespace-nowrap"
      data-testid={testid}
    >
      <div className="relative rounded-2xl bg-white text-[#0B1B4D] font-medium text-sm px-4 py-2 shadow-[0_10px_30px_rgba(30,120,255,0.35)]">
        {line}
        <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 rotate-45 bg-white" />
      </div>
    </motion.div>
  );
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/* -------------------------------------------------------------------------- */
/* Missing-Info Return — Dox comes back thoughtful with one clean ask         */
/* -------------------------------------------------------------------------- */
function NeedsYouCard({ needs, onUploaded, onDismiss }) {
  if (!needs) return null;
  const label = needs.label || "one thing";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.33, 1, 0.68, 1] }}
      className="mt-12 mx-auto max-w-md"
      data-testid="dox-work-needs-you"
    >
      <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300 font-bold mb-3">
        {"I'm almost finished."}
      </div>
      <div className="font-display font-black text-white text-3xl sm:text-4xl tracking-[-0.02em] leading-[1.05]">
        {"I just need your " + label + "."}
      </div>
      <div className="mt-8 flex flex-col items-center gap-3">
        <button
          data-testid="dox-work-needs-upload"
          onClick={onUploaded}
          className="rounded-full bg-dox-orange hover:bg-dox-orangeHover text-white font-semibold text-base px-8 py-4 min-h-[52px] shadow-[0_10px_40px_rgba(30,120,255,0.35)] transition"
        >
          {"I'll upload it."}
        </button>
        <button
          data-testid="dox-work-needs-later"
          onClick={onDismiss}
          className="text-white/40 hover:text-white/70 text-sm transition"
        >
          Not now
        </button>
      </div>
    </motion.div>
  );
}

/* -------------------------------------------------------------------------- */
/* Pixar Visual-Proof Beats — motion inside the WORK phase                    */
/* -------------------------------------------------------------------------- */

/**
 * A small purpose-built animation that plays alongside each work stage.
 * Every beat answers the Pixar rule: "What is Dox doing right now?"
 *   • reviewing  — a page slides across the desk
 *   • verifying  — a stamp descends and hits
 *   • preparing  — a shield glows softly
 *   • filing     — a paper airplane whooshes to the right
 *   • confirming — a green check draws in
 * Falls back to the caller's emoji if no code matches.
 */
function StageVisual({ code, fallbackEmoji }) {
  const size = 34;
  switch (code) {
    case "reviewing":
      return (
        <span className="inline-flex items-center justify-center" style={{ width: size, height: size }} aria-hidden="true">
          <motion.svg width="26" height="30" viewBox="0 0 26 30" fill="none"
            initial={{ x: -14, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.6, ease: [0.33,1,0.68,1] }}>
            <rect x="1.5" y="1.5" width="23" height="27" rx="2.5" fill="#FFFFFF" stroke="#0B1B4D" strokeWidth="1.2" />
            <line x1="5" y1="8" x2="21" y2="8" stroke="#1E78FF" strokeWidth="1.4" strokeLinecap="round" />
            <line x1="5" y1="13" x2="18" y2="13" stroke="#0B1B4D" strokeOpacity=".5" strokeWidth="1" strokeLinecap="round" />
            <line x1="5" y1="17" x2="15" y2="17" stroke="#0B1B4D" strokeOpacity=".4" strokeWidth="1" strokeLinecap="round" />
          </motion.svg>
        </span>
      );
    case "verifying":
      return (
        <span className="inline-flex items-center justify-center" style={{ width: size, height: size }} aria-hidden="true">
          <motion.svg width="28" height="28" viewBox="0 0 28 28" fill="none"
            initial={{ y: -16, scale: 1.2, opacity: 0 }} animate={{ y: [-16, 0, -3, 0], scale: [1.2, 0.95, 1.02, 1], opacity: 1 }}
            transition={{ duration: 0.7, times: [0, 0.55, 0.78, 1], ease: [0.33,1,0.68,1] }}>
            <circle cx="14" cy="14" r="10" fill="#10B981" fillOpacity=".12" stroke="#10B981" strokeWidth="1.5" />
            <path d="M9 14.5 L12.5 18 L19 11" stroke="#10B981" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </motion.svg>
        </span>
      );
    case "preparing":
      return (
        <span className="inline-flex items-center justify-center" style={{ width: size, height: size }} aria-hidden="true">
          <motion.svg width="28" height="30" viewBox="0 0 28 30" fill="none"
            animate={{ filter: ["drop-shadow(0 0 0 rgba(30,120,255,0))", "drop-shadow(0 0 8px rgba(30,120,255,0.85))", "drop-shadow(0 0 2px rgba(30,120,255,0.35))"] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}>
            <path d="M14 2 L25 6 V16 C25 22 20 27 14 29 C8 27 3 22 3 16 V6 Z" fill="#1E78FF" fillOpacity=".18" stroke="#1E78FF" strokeWidth="1.5" strokeLinejoin="round"/>
            <path d="M9 15 L13 19 L20 11" stroke="#1E78FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </motion.svg>
        </span>
      );
    case "filing":
      return (
        <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: size, height: size }} aria-hidden="true">
          <motion.svg width="30" height="20" viewBox="0 0 30 20" fill="none"
            initial={{ x: -14, y: 4, rotate: -10, opacity: 0 }}
            animate={{ x: 20, y: -6, rotate: 10, opacity: [0, 1, 0.9, 0] }}
            transition={{ duration: 1.1, times: [0, 0.25, 0.8, 1], ease: [0.65,0,0.35,1] }}>
            <path d="M2 10 L28 2 L18 18 L14 12 Z" fill="#FFFFFF" stroke="#1E78FF" strokeWidth="1.2" strokeLinejoin="round" />
            <path d="M2 10 L14 12" stroke="#0B1B4D" strokeOpacity=".35" strokeWidth="1" />
          </motion.svg>
        </span>
      );
    case "confirming":
      return (
        <span className="inline-flex items-center justify-center" style={{ width: size, height: size }} aria-hidden="true">
          <motion.svg width="26" height="26" viewBox="0 0 26 26" fill="none">
            <motion.path d="M6 13.5 L11 18 L20 8"
              stroke="#10B981" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
              transition={{ duration: 0.7, ease: [0.33,1,0.68,1] }} />
          </motion.svg>
        </span>
      );
    default:
      return <span className="text-xl" aria-hidden="true">{fallbackEmoji || "•"}</span>;
  }
}
